import type { FC, ReactElement, ReactNode } from "react";
import { Children, cloneElement } from "react";

type TabComponent = "Tab" | "TabList" | "TabPanel" | "DraggableTab";

export type TabFC<T> = FC<T> & {
    tabsRole: TabComponent;
};

function makeTypeChecker(tabsRole: TabComponent) {
    return (element: unknown) =>
        !!(element as ReactElement).type &&
        ((element as ReactElement).type as TabFC<unknown>).tabsRole ===
            tabsRole;
}

export const isTab = makeTypeChecker("Tab");
export const isTabList = makeTypeChecker("TabList");
export const isTabPanel = makeTypeChecker("TabPanel");

export const isFunctionComponent = (element: unknown) =>
    !!(element as ReactElement).type &&
    typeof (element as ReactElement).type === "function";

function isTabChild(child: unknown) {
    return isTab(child) || isTabList(child) || isTabPanel(child);
}

export function mapChild(child: ReactNode, additionalProps: object) {
    if (child === null) return null;

    // Only pass additionalProps to functional components
    // React.cloneElement will throw an error if you pass additionalProps to a dom component like div
    if (isFunctionComponent(child)) {
        return cloneElement(child as ReactElement, {
            ...(child as ReactElement).props,
            ...additionalProps
        });
    }

    return child;
}

export function deepMap(
    children: ReactNode,
    callback: (
        child: string | number | boolean | ReactNode,
        index: number
    ) => ReactNode
): ReactNode {
    return Children.map(children, (child, index) => {
        // null happens when conditionally rendering TabPanel/Tab
        if (child === null) return null;

        if (isTabChild(child)) {
            return callback(child, index);
        }

        if (
            child &&
            (child as ReactElement).props &&
            (child as ReactElement).props.children &&
            typeof (child as ReactElement).props.children === "object"
        ) {
            // Clone the child that has children and map them too
            return cloneElement(child as ReactElement, {
                ...(child as ReactElement).props,
                children: deepMap(
                    (child as ReactElement).props.children,
                    callback
                )
            });
        }

        return child;
    });
}

export function deepForEach(
    children: ReactNode,
    callback: (
        child: string | number | boolean | ReactNode,
        index: number
    ) => void
): void {
    return Children.forEach(children, (child, index) => {
        // null happens when conditionally rendering TabPanel/Tab
        if (child === null) return;

        if (isTab(child) || isTabPanel(child)) {
            callback(child, index);
        } else if (
            child &&
            (child as ReactElement).props &&
            (child as ReactElement).props.children &&
            typeof (child as ReactElement).props.children === "object"
        ) {
            if (isTabList(child)) {
                callback(child, index);
            }
            deepForEach((child as ReactElement).props.children, callback);
        }
    });
}
